import { BaseService } from "@/api/services/baseService";
import request from "@/api";
import type { CustomAxiosRequestConfig } from "@/api";
import type { AxiosResponse } from "axios";
import type {
    OrderVo,
    Page,
    ServiceSpecialVo,
    OrderTonVo,
    ActivityOrderPageParams,
    Order,
    ActivityOrderExportParams,
    RecordOrderForm,
    RecordOrderAuditForm,
    OrderSnapshotVo,
    OrderFinishForm
} from "@zhihaoscm/entity";
import { confirmable } from "@/utils/decorator/confirmable";

export interface OrderFinishConfirmVo {
    /** 是否存在完成后会被自动取消的待发起或待确认提货 */
    hasPendingCancelDelivery: boolean;
    /** 待发起提货数量 */
    toBeInitiatedDeliveryNum: number;
    /** 待确认提货数量 */
    toBeConfirmedDeliveryNum: number;
    /** 待自动取消提货数量 */
    pendingCancelDeliveryNum: number;
}

function getExportFilename(contentDisposition?: string, fallback = "交易订单.xlsx"): string {
    if (!contentDisposition) return fallback;
    const filename =
        contentDisposition
            .split("filename=")[1]
            ?.split(";")[0]
            ?.trim()
            .replace(/^["']|["']$/g, "") ?? "";
    if (!filename) return fallback;
    try {
        return decodeURIComponent(filename);
    } catch {
        return filename;
    }
}

function downloadBlob(blob: Blob, filename: string) {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

export class OrderService extends BaseService<unknown, OrderVo, unknown, unknown> {
    /**
     * 获取专员列表
     */
    public async commissionerList(): Promise<ServiceSpecialVo[]> {
        const { data } = await this.service.get(`${this.path}/find/specials`);
        return data;
    }

    // 指派
    public async batchAssign(params: { orderId: number; handlerId: string }): Promise<unknown> {
        const { orderId, handlerId } = params;
        return await this.service.put(`${this.path}/assign/${orderId}/${handlerId}`);
    }

    public async orderSelector(params: {
        page?: number;
        size?: number;
        keyword?: string;
        states?: number[];
        productId?: string;
        isFilterPayAmount?: number; //是否过滤有支付成功金额的 1是
        isFilterUninvoicedAndReceived?: number; // 是否过滤未开票金额为0且不存在收款记录的订单 1是
    }): Promise<Page<OrderVo>> {
        const { data } = await this.service.get(`${this.path}/selector`, { params });
        return data;
    }

    /**
     * 吨数查询-已售吨数（已拼团吨数）、已提货吨数
     * @param productId 商品编号
     * @param productType 商品类型 （在商品详情时使用） 1寄售 2团购 3捡漏
     */
    public async findTon(productId: string, productType: number): Promise<OrderTonVo> {
        const { data } = await this.service.get(`${this.path}/ton?productId=${productId}&productType=${productType}`);
        return data;
    }

    /**
     * 删除订单
     * */
    @confirmable("是否删除订单，删除后此订单不在展示")
    public async deleteOrder(id: string): Promise<OrderVo> {
        const { data } = await this.service.delete(`${this.path}/${id}`);
        return data;
    }

    /**
     * 取消订单
     * */
    // @confirmable("是否取消订单，取消后此订单作废")
    public async cancelOrder(id: string): Promise<OrderVo> {
        const { data } = await this.service.put(`${this.path}/cancel/${id}`);
        return data;
    }

    /**
     * 完成订单
     * */
    public async finishOrder(id: string, form: OrderFinishForm): Promise<OrderVo> {
        const { data } = await this.service.put(`${this.path}/finish/${id}`, form);
        return data;
    }

    /**
     * 查询完成订单前的二次确认信息，用于决定是否展示提货自动取消的强化提示。
     */
    public async getFinishConfirm(id: string): Promise<OrderFinishConfirmVo> {
        const { data } = await this.service.get(`${this.path}/finish-confirm/${id}`);
        return data;
    }

    /**
     * 从待提货的订单到已完成后：修改已提货吨数
     */
    public async updatePickedUpTon(id: string, form: OrderFinishForm): Promise<OrderVo> {
        const { data } = await this.service.put(`${this.path}/update/ton/${id}`, form);
        return data;
    }

    /**
     * 活动订单分页
     */
    public async getActivityOrderPaging(params: ActivityOrderPageParams): Promise<Page<Order>> {
        const { data } = await this.service.get(`${this.path}/activity/paging`, { params });
        return data;
    }

    /**
     * 导出活动订单
     */
    public async exportActivityOrder(params: ActivityOrderExportParams, filename: string) {
        const response = (await this.service.get(`${this.path}/exportActivityOrder`, {
            params,
            responseType: "blob",
            timeout: 60000
        })) as Blob;
        const url = window.URL.createObjectURL(response);
        const link = document.createElement("a");

        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        return response;
    }

    /**
     * 查询活动专员
     */
    public async findActivitySpecials(): Promise<ServiceSpecialVo[]> {
        const { data } = await this.service.get(`${this.path}/find/activity-specials`);
        return data;
    }

    /**
     * 新增补录订单
     */
    public async createRecordOrder(form: RecordOrderForm) {
        return await this.service.post(`${this.path}/create/record-order`, form);
    }

    /**
     * 编辑补录订单
     */
    public async updateRecordOrder(id: string, form: RecordOrderForm) {
        return await this.service.post(`${this.path}/update/record-order/${id}`, form);
    }

    /**
     * 审核补录订单
     */
    public async auditRecordOrder(id: string, form: RecordOrderAuditForm) {
        return await this.service.put(`${this.path}/audit/record-order/${id}`, form);
    }

    /**
     * 订单快照
     */
    public async orderSnapshot(id: string): Promise<OrderSnapshotVo> {
        const { data } = await this.service.get(`/admin/order-snapshot/vo/${id}`);
        return data;
    }

    /**
     * 导出
     */
    public async export(params: ActivityOrderExportParams, fallbackFilename = "交易订单.xlsx"): Promise<Blob> {
        const response = await this.service.get<Blob, AxiosResponse<Blob>>(`${this.path}/export`, {
            params,
            responseType: "blob",
            returnRawResponse: true
        } as CustomAxiosRequestConfig);
        const filename = getExportFilename(response.headers["content-disposition"], fallbackFilename);
        downloadBlob(response.data, filename);
        return response.data;
    }
}

export const orderService = new OrderService("/admin/order", request.service);
